import React, { useState } from "react";
import { MoreVert } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { DownIcon, UpIcon } from "../../../../../../../../assets/icons";
import { useCurrentTheme } from "../../../../../../../../theme/theme";
import { GreenrouterColumn, GreenrouterRow } from "../../../../data";

type Props = {
	column: GreenrouterColumn;
	columns: GreenrouterColumn[];
	visibleCount: number;
	manageColumnsOpen: boolean,
	setManageColumnsOpen: (val: boolean) => void;
	handleMenuOpen: (col: GreenrouterColumn, anchorEl: HTMLElement) => void;
	handleMenuClose: () => void; 
	onColumnsChange?: (cols: GreenrouterColumn[]) => void;
	sortModel?: {
		field?: keyof GreenrouterRow;
		direction?: "asc" | "desc";
	};
	onSort?: (field: keyof GreenrouterRow, direction?: "asc" | "desc") => void;
};

const SNAP_SIZE = 20; // Snap width increments in px

const SortableHeaderCell = ({ column, columns, visibleCount, manageColumnsOpen, setManageColumnsOpen, handleMenuOpen, handleMenuClose, onColumnsChange, sortModel, onSort }: Props) => {
	const { theme } = useCurrentTheme();

	const isActive = sortModel?.field === column.field;
	const direction = isActive ? sortModel?.direction : undefined;

	const handleSort = (dir?: "asc" | "desc") => {
		onSort?.(column.field, dir);
		handleMenuClose();
	};

	const handleToggleSort = () => {
		if (!isActive) return handleSort("asc");
		return handleSort(direction === "asc" ? "desc" : "asc");
	};

	// Column Resize Logic
	const [isDragging, setIsDragging] = useState(false);

	const startResize = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		setIsDragging(true); // start dragging

		const startX = e.clientX;
		const startWidth = column.width ?? 120;

		const onMouseMove = (ev: MouseEvent) => {
			let newWidth = startWidth + (ev.clientX - startX);
			newWidth = Math.round(newWidth / SNAP_SIZE) * SNAP_SIZE;
			if (newWidth < 80) newWidth = 80;

			onColumnsChange?.(columns.map((c) => (c.field === column.field ? { ...c, width: newWidth } : c)));
		};

		const onMouseUp = () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
			setIsDragging(false); // stop dragging
		};

		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
	};

	const textPaletteColor = theme.palette.primary.contrastText;

	return (
		<Stack
			direction="row"
			width="100%"
			spacing={0.4}
			sx={{
				position: "relative",
				"&:hover .header-menu-icon": {
					visibility: "visible",
					opacity: 1,
				},
				userSelect: "none", // prevent text selection while resizing
			}}
		>
			{/* Column label */}
			<Typography
				variant="body2"
				fontWeight={700}
				sx={{
					cursor: "pointer",
					color: textPaletteColor,
					textOverflow: "ellipsis",
					flex: 1,
				}}
				onClick={handleToggleSort}
			>
				{column.label}
			</Typography>

			{/* Sort arrows */}
			{isActive && direction && (
				<Box
					onClick={(e) => {
						e.stopPropagation();
						handleToggleSort();
					}}
					sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
				>
					{direction === "asc" && <UpIcon sx={{ color: textPaletteColor }} fontSize="small" />}
					{direction === "desc" && <DownIcon sx={{ color: textPaletteColor }} fontSize="small" />}
				</Box>
			)}

			{/* Resize handle */}
			<Box
				onMouseDown={startResize}
				sx={{
					width: 4,
					cursor: "col-resize",
					height: "100%",
					position: "absolute",
					right: -2,
					top: 0,
					zIndex: 2,

					// Default invisible border
					borderRight: `1px solid ${theme.palette.primary.contrastText}`,
					opacity: 0.5,

					// On hover or dragging, show translucent white line
					"&:hover, &.dragging": {
						opacity: 1,
					},

					// Apply dragging class dynamically
					...(isDragging ? { opacity: 1 } : {}),
				}}
			/>

			{/* Menu icon */}
			<Box sx={{ width: 24, display: "flex", justifyContent: "center" }}>
				<IconButton
					size="small"
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
						e.preventDefault();
						handleMenuOpen(column, e.currentTarget);
					}}
					className="header-menu-icon"
					sx={{
						visibility: "hidden",
						opacity: 0,
						transition: "opacity 0.2s",
						p: 0,
					}}
				>
					<MoreVert fontSize="small" sx={{ color: textPaletteColor }} />
				</IconButton>
			</Box>
		</Stack>
	);
};

export default SortableHeaderCell;
